import { useCallback, useContext, useMemo } from "react";

import { getPythonIsoTimestamp } from "../../../utils/get_python_iso_timestamp";
import { DemandWalkEditsContext } from "../../../contexts/demand_walk_edits";
import { Run } from "../../../services/appsync/types";
import { runSettinsToId } from "../../../utils/run_settings_to_id";
import { StartRunButtonComponent } from "./component";
import { StartRunDropdownComponent } from "./dropdown_component";
import { SysMessageLevel } from "../../../contexts/sys_messages/type";
import { SysMessagesContext } from "../../../contexts/sys_messages/context";
import { useApi } from "../../../contexts/app/provider";
import { useCustomer } from "../../../contexts/app/provider";
import { useLockedRun } from "../../../contexts/app/provider";
import { useForceUserEditConclude } from "../../modals/force_user_edit_conclude";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { useModal } from "../../../contexts/modal/hooks";
import { Box, Button } from "@mui/material";
import { SupplyWalkEditsContext } from "../../../contexts/supply_walk_edits/provider";
import { useLocation } from "react-router-dom";
import {
  useGetAllMyEngineSettingsQuery,
  useStartMyUserRunMutation,
  useStartMyRecalculatedUserRunMutation,
} from "../../../services/api/__generated__/backend_gateway-types";
import { LockForecastContext } from "../../../contexts/lock_forecast";

export const StartRunButtonContainer = () => {
  const { isLockUnlockLoading } = useContext(LockForecastContext);
  const { apiService } = useApi();
  const { customerSettings, selectedCustomerId } = useCustomer();
  const { hasLockedRun } = useLockedRun();
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { editMode } = useContext(DemandWalkEditsContext);
  const supplyUnsavedGuard = useContext(SupplyWalkEditsContext);
  const { openModal, closeModal } = useModal();
  const location = useLocation();
  const { openForceUserEditModal } = useForceUserEditConclude();
  const {
    justTriggeredRun,
    setJustTriggeredRun,
    engineInProgress,
    runInProgress,
    googleDriveSyncRuns,
    runsOrdered,
    loading,
  } = useContext(VersionedDataContext);

  const { data: engineSettingsData } = useGetAllMyEngineSettingsQuery({
    client: apiService.getClient(),
    onError: (error) => {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: error.message!,
      });
    },
  });

  const engineSettingsList = useMemo(
    () => engineSettingsData?.getAllMyEngineSettings || [],
    [engineSettingsData]
  );

  const runDisabled = useMemo(() => {
    if (
      engineInProgress ||
      justTriggeredRun ||
      editMode ||
      isLockUnlockLoading
    ) {
      return true;
    }
    return false;
  }, [engineInProgress, justTriggeredRun, editMode, isLockUnlockLoading]);

  // Mutation for locked forecast runs
  const [startMyUserRunMutation] = useStartMyUserRunMutation({
    client: apiService.getClient(),
  });

  const internalStartRun = useCallback(() => {
    if (runDisabled || !customerSettings) {
      return;
    }
    if (!customerSettings.defaultEngineSettingName) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: "No default engine setting found",
      });
      return;
    }
    if (engineSettingsList.length === 0) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: "No engine settings found",
      });
      return;
    }
    const settingsToApply =
      engineSettingsList.find(
        (s) => s.name === customerSettings.defaultEngineSettingName
      ) || engineSettingsList[0];
    const runSettings = {
      inputId:
        googleDriveSyncRuns[0]?.runSettings.inputId || getPythonIsoTimestamp(),
      workflowId: "google_drive_sync",
      settingsNameAndVersion:
        settingsToApply.name + "|" + settingsToApply.version,
      runAttemptId: runsOrdered[0]?.createdAt || getPythonIsoTimestamp(),
    };
    const run: Run = {
      runSettings: {
        ...runSettings,
        organizationId: selectedCustomerId || "",
      },
      status: "RUN_IN_PROGRESS",
      createdAt: new Date().toISOString(),
      stepIds: "[]",
      updatedAt: new Date().toISOString(),
      initiatedBy: null,
      name: null,
      description: null,
      displayId: null,
      atStep: 0,
      totalSteps: 1,
      id: runSettinsToId({
        ...runSettings,
        organizationId: selectedCustomerId || "",
      }),
    };
    setJustTriggeredRun(run);
    startMyUserRunMutation({
      variables: {
        runSettings: {
          inputId: runSettings.inputId,
          workflowId: runSettings.workflowId,
          settingsName: settingsToApply.name,
          runAttemptId: runSettings.runAttemptId,
        },
      },
      onError: (error) => {
        setJustTriggeredRun(null);
        if (error?.message !== undefined) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: error.message!,
          });
        }
      },
    });
  }, [
    runDisabled,
    customerSettings,
    addSystemMessage,
    engineSettingsList,
    googleDriveSyncRuns,
    runsOrdered,
    startMyUserRunMutation,
    setJustTriggeredRun,
    selectedCustomerId,
  ]);

  // Mutation for recalculated forecast runs
  const [startMyRecalculatedUserRunMutation] =
    useStartMyRecalculatedUserRunMutation({
      client: apiService.getClient(),
    });

  // Run with locked forecast (uses existing forecast)

  const handleStartRun = useCallback(() => {
    // If we are on supply walk editable and there are any approved rows, show supply-specific modal
    if (
      location.pathname === "/po-inbox" &&
      (supplyUnsavedGuard?.hasAnyApprovedRows() ||
        supplyUnsavedGuard?.hasUnapprovedEdits())
    ) {
      const onConfirm = () => {
        closeModal();
        // proceed with normal start run flow below after modal
        internalStartRun();
      };

      const onCancel = () => {
        closeModal();
      };
      // Note: onClose not used since modal can be closed via backdrop/escape
      openModal({
        disableStepViewer: true,
        maxWidth: "sm",
        steps: [
          {
            id: "supply_start_run_warning",
            title: "Confirm Start Run",
            content: (
              <Box>
                Please note that unless you exported and imported your approved
                POs into your system, you will not see your PO changes in the
                new run. Only when you go back to this one.
                <br />
                <br />
                To export, click the export button when on the Approved POs tab.
              </Box>
            ),
            customActions: (
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button variant="outlined" onClick={onCancel}>
                  Let me export
                </Button>
                <Button variant="contained" color="primary" onClick={onConfirm}>
                  it's ok, start run
                </Button>
              </Box>
            ),
          },
        ],
      });
      return;
    }
    if (editMode) {
      openForceUserEditModal();
      return;
    }
    if (runDisabled || !customerSettings) {
      return;
    }
    if (!customerSettings.defaultEngineSettingName) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: "No default engine setting found",
      });
      return;
    }

    if (engineSettingsList.length === 0) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: "No engine settings found",
      });
      return;
    }

    const settingsToApply =
      engineSettingsList.find(
        (s) => s.name === customerSettings.defaultEngineSettingName
      ) || engineSettingsList[0];

    const runSettings = {
      inputId:
        googleDriveSyncRuns[0]?.runSettings.inputId || getPythonIsoTimestamp(),
      workflowId: "google_drive_sync",
      settingsNameAndVersion:
        settingsToApply.name + "|" + settingsToApply.version,
      runAttemptId: runsOrdered[0]?.createdAt || getPythonIsoTimestamp(),
    };

    const run: Run = {
      runSettings: {
        ...runSettings,
        organizationId: selectedCustomerId || "",
      },
      status: "RUN_IN_PROGRESS",
      createdAt: new Date().toISOString(),
      stepIds: "[]",
      updatedAt: new Date().toISOString(),
      initiatedBy: null,
      name: null,
      description: null,
      displayId: null,
      atStep: 0,
      totalSteps: 1,
      id: runSettinsToId({
        ...runSettings,
        organizationId: selectedCustomerId || "",
      }),
    };

    setJustTriggeredRun(run);

    // NOTE: this call takes the latest version of the engine settings
    startMyUserRunMutation({
      variables: {
        runSettings: {
          inputId: runSettings.inputId,
          workflowId: runSettings.workflowId,
          settingsName: settingsToApply.name,
          runAttemptId: runSettings.runAttemptId,
        },
      },
      onError: (error) => {
        setJustTriggeredRun(null);
        if (error?.message !== undefined) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: error.message!,
          });
        }
      },
    });
  }, [
    addSystemMessage,
    customerSettings,
    editMode,
    engineSettingsList,
    openForceUserEditModal,
    startMyUserRunMutation,
    runDisabled,
    setJustTriggeredRun,
    googleDriveSyncRuns,
    runsOrdered,
    location.pathname,
    openModal,
    closeModal,
    internalStartRun,
    selectedCustomerId,
    supplyUnsavedGuard,
  ]);

  const internalStartRecalculatedRun = useCallback(() => {
    if (runDisabled || !customerSettings) {
      return;
    }
    if (!customerSettings.defaultEngineSettingName) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: "No default engine setting found",
      });
      return;
    }

    if (engineSettingsList.length === 0) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message: "No engine settings found",
      });
      return;
    }

    const settingsToApply =
      engineSettingsList.find(
        (s) => s.name === customerSettings.defaultEngineSettingName
      ) || engineSettingsList[0];

    const runSettings = {
      inputId:
        googleDriveSyncRuns[0]?.runSettings.inputId || getPythonIsoTimestamp(),
      workflowId: "google_drive_sync",
      settingsNameAndVersion:
        settingsToApply.name + "|" + settingsToApply.version,
      runAttemptId: runsOrdered[0]?.createdAt || getPythonIsoTimestamp(),
    };

    const run: Run = {
      runSettings: {
        ...runSettings,
        organizationId: selectedCustomerId || "",
      },
      status: "RUN_IN_PROGRESS",
      createdAt: new Date().toISOString(),
      stepIds: "[]",
      updatedAt: new Date().toISOString(),
      initiatedBy: null,
      name: null,
      description: null,
      displayId: null,
      atStep: 0,
      totalSteps: 1,
      id: runSettinsToId({
        ...runSettings,
        organizationId: selectedCustomerId || "",
      }),
    };

    setJustTriggeredRun(run);

    // NOTE: this call takes the latest version of the engine settings
    startMyRecalculatedUserRunMutation({
      variables: {
        runSettings: {
          inputId: runSettings.inputId,
          workflowId: runSettings.workflowId,
          settingsName: settingsToApply.name,
          runAttemptId: runSettings.runAttemptId,
        },
      },
      onError: (error) => {
        setJustTriggeredRun(null);
        if (error?.message !== undefined) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: error.message!,
          });
        }
      },
    });
  }, [
    runDisabled,
    customerSettings,
    addSystemMessage,
    engineSettingsList,
    googleDriveSyncRuns,
    runsOrdered,
    startMyRecalculatedUserRunMutation,
    setJustTriggeredRun,
    selectedCustomerId,
  ]);

  // Run with recalculated forecast (recalculates forecast)
  const startRecalculatedRun = useCallback(() => {
    // If we are on supply walk editable and there are any approved rows, show supply-specific modal
    if (
      location.pathname === "/po-inbox" &&
      (supplyUnsavedGuard?.hasAnyApprovedRows() ||
        supplyUnsavedGuard?.hasUnapprovedEdits())
    ) {
      const onConfirm = () => {
        closeModal();
        // proceed with normal start run flow below after modal
        internalStartRecalculatedRun();
      };

      const onCancel = () => {
        closeModal();
      };
      // Note: onClose not used since modal can be closed via backdrop/escape
      openModal({
        disableStepViewer: true,
        maxWidth: "sm",
        steps: [
          {
            id: "supply_start_run_warning",
            title: "Confirm Start Run",
            content: (
              <Box>
                Please note that unless you exported and imported your approved
                POs into your system, you will not see your PO changes in the
                new run. Only when you go back to this one.
                <br />
                <br />
                To export, click the export button when on the Approved POs tab.
              </Box>
            ),
            customActions: (
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button variant="outlined" onClick={onCancel}>
                  Let me export
                </Button>
                <Button variant="contained" color="primary" onClick={onConfirm}>
                  it's ok, start run
                </Button>
              </Box>
            ),
          },
        ],
      });
      return;
    }
    if (editMode) {
      openForceUserEditModal();
      return;
    }
    if (runDisabled || !customerSettings) {
      return;
    }
    internalStartRecalculatedRun();
  }, [
    customerSettings,
    editMode,
    openForceUserEditModal,
    runDisabled,
    location.pathname,
    openModal,
    closeModal,
    internalStartRecalculatedRun,
    supplyUnsavedGuard,
  ]);

  return hasLockedRun ? (
    <StartRunDropdownComponent
      engineInProgress={engineInProgress || justTriggeredRun !== null}
      runInProgress={justTriggeredRun || runInProgress}
      onRunWithLockedForecast={handleStartRun}
      onRunWithRecalculatedForecast={startRecalculatedRun}
      loading={loading || isLockUnlockLoading}
    />
  ) : (
    <StartRunButtonComponent
      engineInProgress={engineInProgress || justTriggeredRun !== null}
      runInProgress={justTriggeredRun || runInProgress}
      onClick={handleStartRun}
      loading={loading || isLockUnlockLoading}
    />
  );
};
